import { formatDate, formatNumber } from '@angular/common';
import { deburr } from 'lodash';
import { ListaDadosAcaoLinha, ListaDadosColuna } from '@app/components/lista-dados/lista-dados.component';
import { HttpClient } from '@angular/common/http';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { SubService } from 'src/app/services/sub.service';
import { SnackBarService } from 'src/app/services/snack-bar.service';
import { BeVeiculoAbastecimento } from '@app/models/BeVeiculoAbastecimento';
import { ConfirmService } from '@app/services/confirm.service';
import { BeVeiculo } from '@app/models/BeVeiculo';
import { BeFormaPagamento } from '@app/models/BeFormaPagamento';
import { BePosto } from '@app/models/BePosto';
import { MatDialog } from '@angular/material/dialog';
import { DialogTextValueComponent } from '@app/components/dialog-text-value/dialog-text-value.component';

@Component({
  selector: 'app-list-veiculo-abastecimentos',
  templateUrl: './list-veiculo-abastecimentos.component.html',
  styleUrls: ['./list-veiculo-abastecimentos.component.css']
})
export class ListVeiculoAbastecimentosComponent implements OnInit, OnDestroy {
  @Input() public veiculo?: BeVeiculo;

  public dados: BeVeiculoAbastecimento[] = [];
  public subscription?: Subscription;
  public postos: BePosto[] = [];
  public formasPagamento: BeFormaPagamento[] = [];
  public filtrarLinha = (linha: BeVeiculoAbastecimento, filtro: string): boolean =>
    [linha.posto?.noReduzido, linha.posto?.dsEndereco, linha.formaPagamento?.dsFormaPagamento,
      linha.txObservacao, linha.dtAbastecimento, linha.qtLitros, linha.vlUnitario, linha.vlTotal, linha.nrKm]
      .some(valor => deburr(`${valor ?? ''}`).toLowerCase().includes(deburr(filtro).toLowerCase()));

  public colunas: ListaDadosColuna[] = [
    { campo: 'idPosto', rotulo: 'Posto', tamanho: '150px', campoExibicao: 'posto.noReduzido',
      tooltip: linha => linha.posto?.dsEndereco ?? '',
      edicao: { tipoEditor: 'combobox', opcoes: [], campoChave: 'idPosto', campoRotulo: 'noReduzido', validacao: { obrigatorio: true } } },
    { campo: 'idFormaPagamento', rotulo: 'Forma Pgto.', tamanho: '130px', campoExibicao: 'formaPagamento.dsFormaPagamento',
      edicao: { tipoEditor: 'combobox', opcoes: [], campoChave: 'idFormaPagamento', campoRotulo: 'dsFormaPagamento', validacao: { obrigatorio: true } } },
    { campo: 'qtLitros', rotulo: 'Litros', tamanho: '100px',
      formatador: (_, valor) => valor == null ? '' : `${formatNumber(valor, 'pt-BR', '1.2-3')} L`,
      edicao: { tipoEditor: 'numero', validacao: { obrigatorio: true, min: 0.001 } } },
    { campo: 'vlUnitario', rotulo: 'Valor Unitário', tamanho: '120px',
      formatador: (_, valor) => valor == null ? '' : `R$ ${formatNumber(valor, 'pt-BR', '1.2-2')}`,
      edicao: { tipoEditor: 'numero', validacao: { obrigatorio: true, min: 0.01 } } },
    { campo: 'vlTotal', rotulo: 'Valor Total', tamanho: '120px',
      formatador: (_, valor) => valor == null ? '' : `R$ ${formatNumber(valor, 'pt-BR', '1.2-2')}`,
      edicao: { tipoEditor: 'numero', validacao: { obrigatorio: true, min: 0.01 } } },
    { campo: 'nrKm', rotulo: 'Km', tamanho: '90px',
      edicao: { tipoEditor: 'numero', validacao: { obrigatorio: true, min: 1 } } },
    { campo: 'dtAbastecimento', rotulo: 'Data', tamanho: '120px',
      formatador: (_, valor) => valor ? formatDate(valor, 'dd/MM/yyyy', 'pt-BR') : '',
      edicao: { tipoEditor: 'data', validacao: { obrigatorio: true } } }
  ];
  public acoesLinha: ListaDadosAcaoLinha[] = [
    { chave: 'observacao', texto: 'Adicionar Observação', icone: 'note_add', visivel: linha => !linha.txObservacao },
    { chave: 'observacao', texto: 'Alterar Observação', icone: 'edit_note', visivel: linha => !!linha.txObservacao }
  ];

  public constructor(
    private httpClient: HttpClient,
    private sub: SubService,
    private snackBarService: SnackBarService,
    private confirmService: ConfirmService,
    private dialog: MatDialog
  ) { }

  public ngOnInit(): void {
    this.loadPostos();
    this.loadFormasPagamento();
    this.refreshData();
  }

  public ngOnDestroy(): void {
    this.unsubscribe();
  }

  public onIniciarEdicaoLinha(linha: BeVeiculoAbastecimento): void {
    linha.idVeiculo = this.veiculo!.idVeiculo!;
    if (!linha.idVeiculoAbastecimento) {
      linha.dtAbastecimento = new Date().toISOString().split('T')[0];
      linha.txObservacao = '';
    }
  }

  public onMudouValorLinha(evento: { linha: BeVeiculoAbastecimento; campo: string }): void {
    if (evento.campo === 'qtLitros' || evento.campo === 'vlUnitario') {
      this.calculateTotal(evento.linha);
    }
  }

  public onDelete(abastecimento: BeVeiculoAbastecimento) {
    this.confirmService.confirm('Deseja excluir esse registro?', 'Alerta', 'Excluir')
      .subscribe(res => {
        if (res) {
          this.sub.reset(this.httpClient.delete(`/api/VeiculoAbastecimentos/${abastecimento.idVeiculoAbastecimento}`),
            () => this.onDeleteConfirm(abastecimento),
            () => this.snackBarService.exibirErro('Não foi possível excluir o registro!')
          );
        }
      });
  }

  public onSalvarLinha(abastecimento: BeVeiculoAbastecimento) {
    this.unsubscribe();

    const data: BeVeiculoAbastecimento = {
      idVeiculo: abastecimento.idVeiculo,
      idPosto: abastecimento.idPosto,
      idFormaPagamento: abastecimento.idFormaPagamento,
      qtLitros: abastecimento.qtLitros,
      vlUnitario: abastecimento.vlUnitario,
      vlTotal: abastecimento.vlTotal,
      nrKm: abastecimento.nrKm,
      txObservacao: abastecimento.txObservacao,
      dtAbastecimento: abastecimento.dtAbastecimento
    };

    if (abastecimento.idVeiculoAbastecimento) {
      data.idVeiculoAbastecimento = abastecimento.idVeiculoAbastecimento;
      this.subscription = this.httpClient.put<BeVeiculoAbastecimento>('/api/VeiculoAbastecimentos/', data).subscribe(this.getCallback(abastecimento, false));
    } else {
      this.subscription = this.httpClient.post<BeVeiculoAbastecimento>('/api/VeiculoAbastecimentos/', data).subscribe(this.getCallback(abastecimento, true));
    }
  }

  public onEditarObservacao(abastecimento: BeVeiculoAbastecimento) {
    this.dialog.open(DialogTextValueComponent, {
      data: {
        title: 'Observação',
        textName: 'txObservacao',
        textTitle: 'Observação',
        textValue: abastecimento.txObservacao || '',
        buttonText: 'Salvar',
        secondButtonText: 'Cancelar',
        idTabelaPai: 'idVeiculoAbastecimento',
        idTabela: abastecimento.idVeiculoAbastecimento!,
        hideNumberField: true
      }
    })
      .afterClosed()
      .subscribe(result => {
        if (result) {
          abastecimento.txObservacao = result.txObservacao;
          this.onSalvarLinha(abastecimento);
        }
      });
  }

  public calculateTotal(abastecimento: BeVeiculoAbastecimento): void {
    if (abastecimento.qtLitros && abastecimento.vlUnitario) {
      abastecimento.vlTotal = abastecimento.qtLitros * abastecimento.vlUnitario;
    }
  }

  private onDeleteConfirm(abastecimento: BeVeiculoAbastecimento) {
    this.dados.splice(this.dados.indexOf(abastecimento), 1);
    this.dados = [...this.dados];
    this.snackBarService.exibirSucesso('Registro excluído!');
    this.unsubscribe();
  }

  private getCallback(abastecimento: BeVeiculoAbastecimento, isPost = false) {
    return {
      next: (res: BeVeiculoAbastecimento) => {
        this.snackBarService.exibirSucesso(`Registro ${isPost ? 'criado' : 'alterado'}!`);
        if (isPost) {
          abastecimento.idVeiculoAbastecimento = res.idVeiculoAbastecimento;
        }
        abastecimento.posto = this.postos.find(p => p.idPosto === abastecimento.idPosto);
        abastecimento.formaPagamento = this.formasPagamento.find(f => f.idFormaPagamento === abastecimento.idFormaPagamento);
        this.dados = [...this.dados];
        this.unsubscribe();
      },
      error: (err: any) => {
        this.snackBarService.exibirErro(`${err.error} - Erro ${err.status} - ${err.statusText}`);
        this.unsubscribe();
      }
    };
  }

  private loadPostos(): void {
    this.httpClient.get<BePosto[]>('/api/Postos').subscribe({
      next: (postos) => {
        this.postos = postos;
        this.colunas[0].edicao!.opcoes = postos;
      },
      error: (err) => {
        this.snackBarService.exibirErro('Erro ao carregar postos');
        console.error('Error loading postos', err);
      }
    });
  }

  private loadFormasPagamento(): void {
    this.httpClient.get<BeFormaPagamento[]>('/api/FormasPagamento').subscribe({
      next: (formasPagamento) => {
        this.formasPagamento = formasPagamento;
        this.colunas[1].edicao!.opcoes = formasPagamento;
      },
      error: (err) => {
        this.snackBarService.exibirErro('Erro ao carregar formas de pagamento');
        console.error('Error loading formas de pagamento', err);
      }
    });
  }

  private unsubscribe() {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = undefined;
    }
  }

  private refreshData(): void {
    if (this.veiculo?.idVeiculo) {
      this.subscription = this.httpClient.get<BeVeiculoAbastecimento[]>(`/api/VeiculoAbastecimentos/ListarPorVeiculo/${this.veiculo.idVeiculo}`).subscribe({
        next: (res) => {
          this.unsubscribe();
          this.onResult(res);
        },
        error: (err) => {
          this.unsubscribe();
          this.snackBarService.exibirErro(`${err.error} - Erro ${err.status} - ${err.statusText}`);
        }
      });
    }
  }

  private onResult(res: BeVeiculoAbastecimento[]): void {
    this.dados = res;
  }
}
